import { useMemo, useRef, useState, type FormEvent, type RefObject } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Brush,
  CalendarDays,
  Check,
  Clock3,
  Leaf,
  Paintbrush,
  PenTool,
  Scissors,
  Search,
  ShieldCheck,
  Sparkle,
  Sparkles,
  Waves,
  type LucideIcon,
} from 'lucide-react';
import { useCategories, useCountries, useSearch, type MarketplaceCategory, type SearchWhen } from '../api/use-marketplace';
import { OWNERS_URL, SiteShell } from '../components/site-shell';
import { SalonCard, SalonCardSkeleton } from '../components/salon-card';
import { CityCombobox } from '../components/city-combobox';
import { COUNTRY_DOMAINS, detectCountryDomain } from '../lib/country-domain';
import { displayRating } from '../lib/salon-meta';
import { MQ, useMediaQuery } from '../lib/use-media-query';
import { canonicalUrl, useDocumentMeta } from '../lib/use-document-meta';
import { Button, EmptyState, Skeleton, SkeletonGroup, cn, focusRing } from '../ui';

/**
 * Marketplace home (board 01 desktop, board 13 mobile):
 *   dark hero (eyebrow · h1 · lead · TREATMENT/WHERE/WHEN search · Popular)
 *   → trust strip → Browse by category → Top rated / Popular near <place>
 *   → Booked in three steps → Run a salon? → footer (shell).
 *
 * Product rules kept: no fake ratings (D-04 — "Popular" heading and "New on
 * Salunas" badges until reviews exist), prices in each salon's own currency,
 * country from the domain (D-08).
 */

const container = 'mx-auto w-full max-w-[1360px] px-gutter-m md:px-8 lg:px-gutter';

const POPULAR_KEYS = ['haircut', 'beardTrim', 'manicure', 'facial', 'massage'] as const;

/** Line icon per category key (board 01 tiles). Keys come from the API, so match loosely. */
function categoryIcon(key: string): LucideIcon {
  const k = key.toLowerCase();
  if (k.includes('barber')) return Brush;
  if (k.includes('hair')) return Scissors;
  if (k.includes('nail')) return Paintbrush;
  if (k.includes('beauty') || k.includes('face') || k.includes('skin')) return Sparkle;
  if (k.includes('massage')) return Waves;
  if (k.includes('spa') || k.includes('well')) return Leaf;
  if (k.includes('tattoo') || k.includes('pierc')) return PenTool;
  return Sparkles;
}

export function HomePage() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: t('meta.home.title'),
    description: t('meta.home.description'),
    canonical: canonicalUrl('/'),
  });
  const cityInputRef = useRef<HTMLInputElement>(null);
  const cats = useCategories();
  const categories = useMemo(() => (cats.data ?? []).filter((c) => c.key !== 'other'), [cats.data]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  return (
    <SiteShell>
      <Hero cityInputRef={cityInputRef} />
      <TrustStrip />
      <div className={cn(container, 'flex flex-col gap-10 py-8 md:gap-[50px] md:py-[50px]')}>
        <CategorySection
          categories={categories}
          loading={cats.isLoading}
          selected={selectedCategory}
          onSelect={setSelectedCategory}
        />
        <TopRatedSection
          category={selectedCategory}
          categories={categories}
          onChangeLocation={() => {
            cityInputRef.current?.scrollIntoView?.({ block: 'center' });
            cityInputRef.current?.focus();
          }}
        />
        <StepsPanel />
        <OwnerPanel />
      </div>
    </SiteShell>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────

function useEyebrowPlace(): string {
  const { t, i18n } = useTranslation();
  const ctx = detectCountryDomain();
  const countries = useCountries();
  const nameOf = (code: string) => {
    const key = COUNTRY_DOMAINS.find((d) => d.code === code)?.i18nKey;
    return key ? t('countrySwitcher.' + key) : null;
  };
  if (ctx.code) return nameOf(ctx.code) ?? t('countrySwitcher.all');
  // salunas.com: the countries we actually have salons in (D-08 ".com → all").
  const names = (countries.data ?? [])
    .filter((c) => c.salonCount > 0)
    .map((c) => nameOf(c.code))
    .filter((n): n is string => !!n);
  if (!names.length) return t('countrySwitcher.all');
  try {
    return new Intl.ListFormat(i18n.resolvedLanguage || 'en', { type: 'conjunction' }).format(names);
  } catch {
    return names.join(' & ');
  }
}

function Hero({ cityInputRef }: { cityInputRef: RefObject<HTMLInputElement | null> }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const desktop = useMediaQuery(MQ.md);
  const place = useEyebrowPlace();
  const [service, setService] = useState('');
  // Pre-fill the city from the country domain; never a Belgian default elsewhere.
  const [city, setCity] = useState(detectCountryDomain().defaultCity);
  const [when, setWhen] = useState<SearchWhen>('any');

  const go = (q: string) => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (city.trim()) params.set('city', city.trim());
    if (when !== 'any') params.set('when', when);
    nav(`/search${params.toString() ? `?${params.toString()}` : ''}`);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    go(service);
  };

  const whenSelect = (className: string, id: string) => (
    <select
      id={id}
      value={when}
      onChange={(e) => setWhen(e.target.value as SearchWhen)}
      className={cn('w-full min-w-0 cursor-pointer bg-transparent text-sl-ink focus:outline-none', className)}
    >
      <option value="any">{t('search.when.any')}</option>
      <option value="today">{t('search.when.today')}</option>
      <option value="7d">{t('search.when.next7')}</option>
    </select>
  );

  return (
    <section className="bg-sl-dark text-sl-on-dark">
      <div className={cn(container, 'pb-6 pt-[26px] md:pb-16 md:pt-20 md:text-center')}>
        <p className="mb-5 hidden items-center justify-center gap-2 text-[11.5px] font-semibold uppercase tracking-[3px] text-sl-hero-accent md:flex">
          <Sparkle className="h-3 w-3 fill-current" aria-hidden="true" />
          {t('home.eyebrow', { place })}
        </p>
        <h1 className="font-display text-[31px] font-semibold leading-[1.12] md:mx-auto md:max-w-[620px] md:text-[56px] md:font-medium md:leading-[1.08]">
          <Trans
            i18nKey="home.title"
            components={{
              // Mobile: own line, no italic/accent (board 13); the period stays attached.
              em: <em className="not-italic max-md:before:whitespace-pre max-md:before:content-['\A'] md:italic md:text-sl-hero-accent" />,
            }}
          />
        </h1>
        <p className="mt-2 text-[14px] leading-snug text-sl-on-dark-muted md:mx-auto md:mt-5 md:max-w-[540px] md:text-[17px] md:leading-[1.55]">
          <span className="md:hidden">{t('home.leadMobile')}</span>
          <span className="hidden md:inline">{t('home.lead')}</span>
        </p>

        <form
          role="search"
          aria-label={t('search.formLabel')}
          onSubmit={submit}
          className="mt-5 rounded-18 bg-sl-surface p-3 text-left text-sl-ink md:mx-auto md:shadow-hero md:mt-10 md:flex md:max-w-[860px] md:items-center md:p-2"
        >
          {desktop ? (
            <>
              <label className="flex min-w-0 flex-[1.25] cursor-text flex-col px-5 py-2.5">
                <span className="text-[10.5px] font-bold uppercase tracking-[1.6px] text-sl-label-2">
                  {t('home.fields.treatment')}
                </span>
                <input
                  type="search"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  placeholder={t('home.fields.treatmentPlaceholder')}
                  className="mt-1 w-full bg-transparent text-[15px] text-sl-ink placeholder:text-sl-label-2 focus:outline-none"
                />
              </label>
              <span aria-hidden="true" className="h-10 w-px bg-sl-line" />
              <CityCombobox
                ref={cityInputRef}
                variant="segment"
                label={t('home.fields.where')}
                value={city}
                onChange={setCity}
                placeholder={t('search.locationPlaceholder')}
                className="flex-1"
              />
              <span aria-hidden="true" className="h-10 w-px bg-sl-line" />
              <label htmlFor="home-when" className="flex min-w-0 flex-1 cursor-pointer flex-col px-5 py-2.5">
                <span className="text-[10.5px] font-bold uppercase tracking-[1.6px] text-sl-label-2">
                  {t('home.fields.when')}
                </span>
                {whenSelect('mt-1 text-[15px]', 'home-when')}
              </label>
              <Button
                type="submit"
                variant="accent"
                className="h-[51px] shrink-0 rounded-12 px-[26px] text-[15px] font-bold"
                trailingIcon={<ArrowRight className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />}
              >
                {t('common.search')}
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-2.5">
              <label className="flex h-ctl-md items-center gap-2.5 rounded-12 border border-sl-field-line bg-sl-surface px-3.5 focus-within:border-sl-accent">
                <Search className="h-4 w-4 shrink-0 text-sl-label" strokeWidth={1.8} aria-hidden="true" />
                <input
                  type="search"
                  aria-label={t('home.fields.treatment')}
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  placeholder={t('home.fields.treatmentPlaceholderMobile')}
                  className="w-full min-w-0 bg-transparent text-[14.5px] text-sl-ink placeholder:text-sl-label-2 focus:outline-none"
                />
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <CityCombobox
                  ref={cityInputRef}
                  variant="field"
                  label={t('home.fields.where')}
                  value={city}
                  onChange={setCity}
                  placeholder={t('search.locationPlaceholder')}
                />
                <label
                  htmlFor="home-when-m"
                  className="flex h-ctl-md items-center gap-2.5 rounded-12 border border-sl-field-line bg-sl-surface px-3.5 focus-within:border-sl-accent"
                >
                  <CalendarDays className="h-4 w-4 shrink-0 text-sl-label" strokeWidth={1.8} aria-hidden="true" />
                  <span className="sr-only">{t('home.fields.when')}</span>
                  {whenSelect('text-[14.5px]', 'home-when-m')}
                </label>
              </div>
              <Button
                type="submit"
                variant="accent"
                fullWidth
                className="h-12 rounded-12 text-[15px] font-bold"
                trailingIcon={<ArrowRight className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />}
              >
                {t('common.search')}
              </Button>
            </div>
          )}
        </form>

        <div className="mt-6 hidden flex-wrap items-center justify-center gap-2 md:flex">
          <span className="mr-1 text-[13px] text-sl-on-dark-faint">{t('home.popular.label')}</span>
          {POPULAR_KEYS.map((k) => {
            const term = t(`home.popular.${k}`);
            return (
              <button
                key={k}
                type="button"
                onClick={() => go(term)}
                className={cn(
                  'rounded-full border border-sl-on-dark/25 px-3.5 py-[7px] text-[13px] font-medium text-sl-on-dark-chip transition-colors hover:border-sl-on-dark/60 hover:text-white',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sl-hero-accent focus-visible:ring-offset-2 focus-visible:ring-offset-sl-dark',
                )}
              >
                {term}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Trust strip ───────────────────────────────────────────────────────

function TrustStrip() {
  const { t } = useTranslation();
  const items: Array<{ icon: LucideIcon; label: string }> = [
    { icon: Check, label: t('home.trust.verified') },
    { icon: Clock3, label: t('home.trust.realtime') },
    { icon: ShieldCheck, label: t('home.trust.guest') },
  ];
  return (
    // Board 13 drops the strip on mobile (hero runs straight into the category pills).
    <div className="hidden border-b border-sl-line bg-sl-surface md:block">
      <ul className={cn(container, 'flex min-h-[51px] flex-row items-center justify-center gap-11')}>
        {items.map(({ icon: Icon, label }) => (
          <li key={label} className="flex items-center gap-2 text-[13px] text-sl-body-2">
            <Icon className="h-3.5 w-3.5 shrink-0 text-sl-success" strokeWidth={2.2} aria-hidden="true" />
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Categories ────────────────────────────────────────────────────────

function SectionHead({
  title,
  sub,
  action,
  id,
}: {
  title: string;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  id: string;
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4 md:mb-5">
      <div className="min-w-0">
        <h2 id={id} className="font-display text-[20px] font-semibold leading-tight text-sl-ink md:text-[28px] md:font-medium">
          {title}
        </h2>
        {sub && <p className="mt-1 text-[13px] text-sl-muted">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

const textLink = cn(
  'inline-flex shrink-0 items-center gap-1 rounded-sm text-[13.5px] font-semibold text-sl-link hover:text-sl-link-hover',
  focusRing,
);

function CategorySection({
  categories,
  loading,
  selected,
  onSelect,
}: {
  categories: MarketplaceCategory[];
  loading: boolean;
  selected: string | null;
  onSelect: (key: string | null) => void;
}) {
  const { t } = useTranslation();
  const desktop = useMediaQuery(MQ.md);

  if (!desktop) {
    // Board 13: horizontal pills that filter the salon list in place.
    if (!loading && categories.length === 0) return null;
    const pills: Array<{ key: string | null; label: string }> = [
      { key: null, label: t('home.categoryAll') },
      ...categories.map((c) => ({ key: c.key, label: c.displayName })),
    ];
    return (
      <nav aria-label={t('home.browseHeading')} className="-mx-gutter-m -mb-4">
        <div className="-mt-[3px] flex gap-2 overflow-x-auto px-gutter-m pb-1 pt-[3px]">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} radius="pill" className="h-[38px] w-20 shrink-0" />)
            : pills.map((p) => {
                const active = p.key === selected;
                return (
                  <button
                    key={p.key ?? 'all'}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onSelect(p.key)}
                    className={cn(
                      'hit-44 h-[38px] shrink-0 whitespace-nowrap rounded-full px-4 text-[13px] transition-colors',
                      focusRing,
                      active ? 'bg-sl-ink font-semibold text-white' : 'border border-sl-line-strong bg-sl-surface font-medium text-sl-ink',
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
        </div>
      </nav>
    );
  }

  return (
    <section aria-labelledby="home-categories">
      <SectionHead
        id="home-categories"
        title={t('home.browseHeading')}
        action={
          <Link to="/search" className={textLink}>
            {t('home.allCategories')}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden="true" />
          </Link>
        }
      />
      {loading ? (
        <div className="grid grid-cols-4 gap-3.5 lg:grid-cols-7">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} radius={16} className="h-[90px]" />
          ))}
        </div>
      ) : (
        <ul className="grid grid-cols-4 gap-3.5 lg:grid-cols-7">
          {categories.map((c) => {
            const Icon = categoryIcon(c.key);
            const active = c.key === selected;
            return (
              <li key={c.key}>
                <Link
                  to={`/search?category=${encodeURIComponent(c.key)}`}
                  className={cn(
                    'flex h-[90px] flex-col items-center justify-center gap-2 rounded-16 border px-2 text-center text-[13.5px] font-semibold transition-colors',
                    focusRing,
                    active
                      ? 'border-sl-accent bg-sl-accent-tint text-sl-link'
                      : 'border-sl-line bg-sl-surface text-sl-ink hover:border-sl-line-strong',
                  )}
                >
                  <Icon className={cn('h-[18px] w-[18px]', active ? 'text-sl-link' : 'text-sl-ink')} strokeWidth={1.6} aria-hidden="true" />
                  <span className="leading-tight">{c.displayName}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ─── Top rated / Popular ───────────────────────────────────────────────

function TopRatedSection({
  category,
  categories,
  onChangeLocation,
}: {
  category: string | null;
  categories: MarketplaceCategory[];
  onChangeLocation: () => void;
}) {
  const { t } = useTranslation();
  const ctx = detectCountryDomain();
  const city = ctx.defaultCity || null;
  const base = { country: ctx.code, category, size: 6, nextSlots: 3, sort: 'recommended' as const, page: 0 };

  // Near the domain's default city first; if that city has nothing yet, fall
  // back to the whole country so the section never looks empty for no reason.
  const near = useSearch({ ...base, city });
  const needFallback = !!city && near.isSuccess && near.data.results.length === 0;
  const wide = useSearch(base, { enabled: needFallback });
  const active = needFallback ? wide : near;
  const rows = active.data?.results ?? [];
  const loading = near.isLoading || (needFallback && wide.isLoading);

  const countryKey = ctx.code ? COUNTRY_DOMAINS.find((d) => d.code === ctx.code)?.i18nKey : null;
  const placeCity = needFallback ? null : city;
  const placeCountry = countryKey ? t('countrySwitcher.' + countryKey) : null;
  const rated = rows.some((r) => displayRating(r) != null);
  const kind = rated ? 'topRated' : 'popularSalons';
  const title = placeCity
    ? t(`home.${kind}.near`, { place: placeCity })
    : placeCountry
      ? t(`home.${kind}.in`, { place: placeCountry })
      : t(`home.${kind}.all`);
  const seeAll = placeCity ? `/search?city=${encodeURIComponent(placeCity)}` : '/search';
  const catLabel = category ? categories.find((c) => c.key === category)?.displayName ?? category : null;

  return (
    <section aria-labelledby="home-salons">
      <SectionHead
        id="home-salons"
        title={title}
        sub={
          <>
            <span className="hidden md:inline">
              {rated ? t('home.sortedRated') : t('home.sortedPopular')}
              <span aria-hidden="true"> · </span>
            </span>
            <button type="button" onClick={onChangeLocation} className={cn(textLink, 'hidden text-[13px] md:inline-flex')}>
              {t('home.changeLocation')}
            </button>
          </>
        }
        action={
          <Link to={seeAll} className={textLink}>
            <span className="md:hidden">{t('home.seeAllShort')}</span>
            <span className="hidden md:inline">{t('home.seeAll')}</span>
            <ArrowRight className="hidden h-3.5 w-3.5 md:block" strokeWidth={1.8} aria-hidden="true" />
          </Link>
        }
      />
      {loading ? (
        <SkeletonGroup className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <SalonCardSkeleton key={i} />
          ))}
        </SkeletonGroup>
      ) : rows.length === 0 ? (
        <EmptyState
          size="section"
          titleAs="h3"
          title={catLabel ? t('home.salonsNoCategory', { label: catLabel.toLocaleLowerCase() }) : t('home.salonsNoListed')}
          description={t('home.salonsCuratingBody')}
          className="rounded-20 border border-dashed border-sl-line-dashed px-6 py-12"
        />
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-[22px] lg:grid-cols-3">
          {rows.map((s) => (
            <li key={s.key}>
              <SalonCard salon={s} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Steps + owner panel ───────────────────────────────────────────────

function StepsPanel() {
  const { t } = useTranslation();
  const steps = [1, 2, 3] as const;
  return (
    <section aria-labelledby="home-steps" className="rounded-24 bg-sl-sand-2 px-6 py-9 md:px-[52px] md:py-11">
      <h2 id="home-steps" className="text-center font-display text-[22px] font-medium text-sl-ink md:text-[28px]">
        {t('home.steps.title')}
      </h2>
      <ol className="mt-7 grid grid-cols-1 gap-7 md:mt-8 md:grid-cols-3 md:gap-10">
        {steps.map((n) => (
          <li key={n} className="text-center">
            <span aria-hidden="true" className="block font-display text-[38px] font-normal italic leading-none text-sl-accent">
              {n}
            </span>
            <h3 className="mt-3 text-[16px] font-semibold text-sl-ink">{t(`home.steps.s${n}Title`)}</h3>
            <p className="mx-auto mt-1.5 max-w-[320px] text-[14px] leading-[1.55] text-sl-body-2">{t(`home.steps.s${n}Body`)}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function OwnerPanel() {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="home-owner"
      className="flex flex-col gap-6 rounded-24 bg-sl-dark px-6 py-8 text-sl-on-dark md:flex-row md:items-center md:justify-between md:px-[52px] md:py-[42px]"
    >
      <div className="max-w-[620px]">
        <h2 id="home-owner" className="font-display text-[24px] font-medium md:text-[30px]">
          {t('home.owner.title')}
        </h2>
        <p className="mt-2 text-[14.5px] leading-[1.6] text-sl-on-dark-muted">{t('home.owner.body')}</p>
      </div>
      <div className="flex shrink-0 flex-col items-start gap-2.5 md:items-center">
        <Button
          href={OWNERS_URL}
          target="_blank"
          variant="light"
          className="h-12 px-[26px] text-[15px]"
          trailingIcon={<ArrowRight className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />}
        >
          {t('home.owner.cta')}
        </Button>
        <p className="text-[12px] text-sl-on-dark-faint">{t('home.owner.fine')}</p>
      </div>
    </section>
  );
}
